'use client';

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Box, Stack, Typography, Link, useBreakpoints, Theme, Sheet } from '@castlery/fortress';
import { useAppSelector } from '@castlery/shared-redux-store';
import { selectProduct, selectVariant } from '@castlery/modules-product-domain';
import { basePageConfig, EcEnv, HULLA_EXPERIENCE_LABEL } from '@castlery/config';
import { CustomLink, FortressImage, FortressVideo } from '@castlery/shared-components';
import { useGetDyCampaignsQuery } from '@castlery/modules-dy-domain';
import { formatCampaignsResponse, hintegrateLoader } from '@castlery/utils';
import { ArrowRight } from '@castlery/fortress/Icons';
import { logger } from '@castlery/observability';

interface ModularTool {
  modular_tool_url?: string;
  configuratorToolBannerDesktop?: string | VideoInfo;
  configuratorToolBannerMobile?: string | VideoInfo;
}

interface NewRoomTool {
  furniture_tool_url?: string;
  roomDesignerBannerDesktop?: string | VideoInfo;
  roomDesignerBannerMobile?: string | VideoInfo;
}

interface VideoInfo {
  videoPath: string;
}

interface RoomDesignerABTestItem {
  image_link: string;
  click_link: string;
}

// 背景图片配置
const blankBackgrounds = {
  SG: 'https://res.cloudinary.com/castlery/image/upload/v1684378872/static/room-designer/banner_background_sg.jpg',
  AU: 'https://res.cloudinary.com/castlery/image/upload/v1684379481/static/room-designer/banner_background_au.jpg',
  US: 'https://res.cloudinary.com/castlery/image/upload/v1684379485/static/room-designer/banner_background_us.jpg',
  CA: 'https://res.cloudinary.com/castlery/image/upload/v1684379485/static/room-designer/banner_background_us.jpg',
  UK: 'https://res.cloudinary.com/castlery/image/upload/v1684379485/static/room-designer/banner_background_us.jpg',
} as const;

const roomDesignerSelector = 'Room Designer';
const modularToolSelector = 'Modular Tool';

export const ProductHullabala = () => {
  const { desktop, mobile } = useBreakpoints();
  const product = useAppSelector(selectProduct);
  const variant = useAppSelector(selectVariant);
  // const [getDyCampaigns, { data: campaignsData, isLoading }] = useLazyGetDyCampaignsQuery();
  // 状态管理
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isTestShow, setTestShow] = useState(false);
  const [isExist, setIsExist] = useState(false);
  const [modularTool, setModularTool] = useState<ModularTool>({});
  const [newRoomTool, setNewRoomTool] = useState<NewRoomTool>({});
  const [modularShow, setModularShow] = useState(false);
  const [newRoomShow, setNewRoomShow] = useState(false);
  const [observeOnce, setObserveOnce] = useState(false);

  const observerRef = useRef<HTMLDivElement>(null);

  const queryParams = useMemo(() => {
    const campaignNames = [modularToolSelector];
    if (EcEnv.NEXT_PUBLIC_COUNTRY === 'US') {
      campaignNames.push(roomDesignerSelector);
    }
    return {
      campaignNames,
      // recommendationContext: {
      //   type: DYPageTypes.PRODUCT,
      //   data: [variant?.sku || ''],
      // },
    };
  }, [variant?.sku]);

  const { data: campaignsData, isLoading } = useGetDyCampaignsQuery(queryParams, {
    skip: !variant?.sku, // 跳过条件
  });

  // 格式化 DY 数据
  const formattedCampaigns = useMemo(() => {
    if (!campaignsData?.choices) return null;
    return formatCampaignsResponse(campaignsData.choices);
  }, [campaignsData]);

  // 获取当前 selector 的 promotion 数据
  const dyRoomDesigner = useMemo(() => {
    if (!formattedCampaigns || !formattedCampaigns[roomDesignerSelector]) return null;
    const result = formattedCampaigns[roomDesignerSelector]?.hitVariation;
    if (result && typeof result === 'object' && Object.keys(result).length === 0) {
      return undefined;
    }
    return result;
  }, [formattedCampaigns]);

  const dyModularTool = useMemo(() => {
    if (!formattedCampaigns || !formattedCampaigns[modularToolSelector]) return null;
    const result = formattedCampaigns[modularToolSelector]?.hitVariation;
    if (result && typeof result === 'object' && Object.keys(result).length === 0) {
      return undefined;
    }
    return result;
  }, [formattedCampaigns]);

  // 构建campaign名称数组
  // const campaignNames = useMemo(() => {
  //   const names = [modularToolSelector];
  //   if (EcEnv.NEXT_PUBLIC_COUNTRY === 'US') {
  //     names.push(roomDesignerSelector);
  //   }
  //   return names;
  // }, []);

  // 使用 useEffect 来调用 DY campaigns API
  // useEffect(() => {
  //   if (campaignNames.length > 0) {
  //     getDyCampaigns({
  //       campaignNames,
  //       recommendationContext: {
  //         type: DYPageTypes.PRODUCT,
  //       },
  //     });
  //   }
  // }, [campaignNames, getDyCampaigns]);

  // 响应式 Room Designer A/B Test 配置
  const RoomDesignerABTestResponsive = useMemo(
    () =>
      new Map<string, RoomDesignerABTestItem>([
        [
          'Auburn',
          {
            image_link: desktop
              ? 'https://res.cloudinary.com/castlery/image/upload/f_auto,q_auto/v1/New%20Room%20Designer/bdz8nfd8zt0thqb3qi2n'
              : 'https://res.cloudinary.com/castlery/image/upload/f_auto,q_auto/v1/New%20Room%20Designer/qti4yyzgxusuvxp7mdfy',
            click_link:
              'https://www.castlery.com/us/room-designer?&gallery=truep=20027%2CaHR0cHM6Ly9yZXMuY2xvdWRpbmFyeS5jb20vY2FzdGxlcnkvaW1hZ2UvcHJpdmF0ZS9lX3RyaW06MTUsYl90cmFuc3BhcmVudCxjX2ZpdCxmX2F1dG8scV9hdXRvLHdfNzUwL3YxNjg0MjMzNDU5L2NydXNhZGVyL3ZhcmlhbnRzLzQwMzcwMDQyL0Rlc2ktV29vbC1SdWctOF8teC0xMF8tU3BlY2tsZWQtQmVpZ2UtMTY4NDIzMzQ1OS5qcGc%3D%2C244%2C305%2C497%2C458%2Cn%2Cn%2Cundefined%2C0%2CRugs%3Arectangle%2CNaN%2C%2C%2C&p=21441%2C501%2C353%2Cn%2Cn%2C0%2C%2CNaN%2C%2C%2C&p=21220%2C489%2C437%2Cn%2Cn%2C0%2C%2CNaN%2C%2C%2C&p=21439%2C218%2C435%2Cn%2Cn%2C0%2C%2CNaN%2C%2C%2C&p=22080%2C746.6124661246613%2C290.2845528455284%2Cn%2Cn%2C0%2C%2CNaN%2C%2C%2C&p=21611%2C766%2C420%2Cn%2Cn%2C0%2C%2CNaN%2C%2C%2C&w=%23cdc6b3&f=%23987759&s=595&h=297&t=wood&d=plain&V=1&b=f&i=us-3',
          },
        ],
        [
          'Owen',
          {
            image_link: desktop
              ? 'https://res.cloudinary.com/castlery/image/upload/f_auto,q_auto/v1/New%20Room%20Designer/mbndtjaevew9idlss34r'
              : 'https://res.cloudinary.com/castlery/image/upload/f_auto,q_auto/v1/New%20Room%20Designer/p5mg7rpai5cjryltihq9',
            click_link:
              'https://www.castlery.com/us/room-designer?gallery=true&_gl=1*qna9jr*_ga*MTcwOTcxMjA3NC4xNzAyODkxMjc1*_ga_YM2B9443EJ*MTcwMjg5MTI3NC4xLjEuMTcwMjg5MTg0OS40NC4wLjA.&p=20022%2CaHR0cHM6Ly9yZXMuY2xvdWRpbmFyeS5jb20vY2FzdGxlcnkvaW1hZ2UvcHJpdmF0ZS9lX3RyaW06MTUsYl90cmFuc3BhcmVudCxjX2ZpdCxmX2F1dG8scV9hdXRvLHdfNzUwL3YxNjg0Mjg5OTIzL2NydXNhZGVyL3ZhcmlhbnRzLzQwMzcwMDM4L1ZlbnR1cmEtSnV0ZS1SdWctOF8teC0xMF8tTmF0dXJhbC1CbGFjay0xNjg0Mjg5OTIzLmpwZw%3D%3D%2C244%2C305%2C532%2C436%2Cn%2Cn%2Cundefined%2C0%2CRugs%3Arectangle%2C0%2C%2C%2C&p=20610%2C569%2C412%2Cn%2Cn%2C0%2C%2C0%2C%2C%2C&p=19560%2C192%2C238%2Cn%2Cn%2C0%2C%2C0%2C%2C%2C&p=21266%2C516%2C336%2Cn%2Cn%2C0%2C%2C0%2C%2C%2C&p=22082%2C225.71263259402124%2C200.14079074252655%2Cn%2Cn%2C0%2C%2C0%2C%2C%2C&p=22436%2C847%2C402%2Cn%2Cn%2C0%2C%2C0%2C%2C%2C&w=%23efe5da&f=%23EAC9A4&s=579&h=297&t=wood&d=plain&V=1&b=f&i=',
          },
        ],
        [
          'Jonathan',
          {
            image_link: desktop
              ? 'https://res.cloudinary.com/castlery/image/upload/f_auto,q_auto/v1/New%20Room%20Designer/yn4wh0gpuy2ehmiyjoqu'
              : 'https://res.cloudinary.com/castlery/image/upload/f_auto,q_auto/v1/New%20Room%20Designer/rgxdk9wavq201jid1vug',
            click_link:
              'https://www.castlery.com/us/room-designer?logo=no&gallery=true&p=20039%2CaHR0cHM6Ly9yZXMuY2xvdWRpbmFyeS5jb20vY2FzdGxlcnkvaW1hZ2UvcHJpdmF0ZS9lX3RyaW06MTUsYl90cmFuc3BhcmVudCxjX2ZpdCxmX2F1dG8scV9hdXRvLHdfNzUwL3YxNjg0MjMzMTgzL2NydXNhZGVyL3ZhcmlhbnRzLzQwMzcwMDUwL0F6aXphLVJ1Zy04Xy14LTEwXy1XaGl0ZS1CbGFjay0xNjg0MjMzMTgyLmpwZw%3D%3D%2C244%2C305%2C501%2C457%2Cn%2Cn%2Cundefined%2C0%2CRugs%3Arectangle%2CNaN%2C%2C%2C&p=20596%2C321%2C368%2Cn%2Cn%2C0%2C%2CNaN%2C%2C%2C&p=20292%2C222%2C402%2Cn%2Cn%2C0%2C%2C345%2C50485195%2C58938822%2Cc8963b2e232b9833cfdc625fda4259b02e875bad5a4e929452d1039a0dfd9241&p=22076%2C325%2C273%2Cn%2Cn%2C0%2C%2CNaN%2C%2C%2C&p=21309%2C618%2C390%2Cn%2Cn%2C0%2C%2CNaN%2C%2C%2C&p=20610%2C544%2C435%2Cn%2Cn%2C0%2C%2CNaN%2C%2C%2C&w=%23b0b0b0&f=%23EAC9A4&s=595&h=297&t=wood&d=plain&V=1&b=f&i=us-1',
          },
        ],
        [
          'Madison',
          {
            image_link: desktop
              ? 'https://res.cloudinary.com/castlery/image/upload/f_auto,q_auto/v1/New%20Room%20Designer/yymvatdkjtgzq8rh6bla'
              : 'https://res.cloudinary.com/castlery/image/upload/f_auto,q_auto/v1/New%20Room%20Designer/ghb7bctt0vttt6oyesx6',
            click_link:
              'https://www.castlery.com/us/room-designer?gallery=truep%3D20027%2CaHR0cHM6Ly9yZXMuY2xvdWRpbmFyeS5jb20vY2FzdGxlcnkvaW1hZ2UvcHJpdmF0ZS9lX3RyaW06MTUsYl90cmFuc3BhcmVudCxjX2ZpdCxmX2F1dG8scV9hdXRvLHdfNzUwL3YxNjg0MjMzNDU5L2NydXNhZGVyL3ZhcmlhbnRzLzQwMzcwMDQyL0Rlc2ktV29vbC1SdWctOF8teC0xMF8tU3BlY2tsZWQtQmVpZ2UtMTY4NDIzMzQ1OS5qcGc%3D%2C244%2C305%2C454.2302357836338%2C454.2787794729543%2Cn%2Cn%2Cundefined%2C0%2CRugs%3Arectangle%2Cundefined%2C%2C%2C&p=17031%2C834%2C333%2Cn%2Cn%2C0%2C%2CNaN%2C%2C%2C&p=18893%2C193%2C362%2Cn%2Cn%2C0%2C%2CNaN%2C%2C%2C&p=20275%2C469%2C351%2Cn%2Cn%2C0%2C%2CNaN%2C%2C%2C&p=21166%2C538%2C434%2Cn%2Cn%2C0%2C%2CNaN%2C33808816%2C40181545%2C918610ad408d3622c6dc5daff332d3a3efefcf235247cac114a7c309a11c0df1&w=%23b0b0b0&f=%23EAC9A4&s=595&h=297&t=wood&d=plain&V=1&b=f&i=us-3',
          },
        ],
        [
          'Adams',
          {
            image_link: desktop
              ? 'https://res.cloudinary.com/castlery/image/upload/f_auto,q_auto/v1/New%20Room%20Designer/opsup1ztjbdase5hlixc'
              : 'https://res.cloudinary.com/castlery/image/upload/f_auto,q_auto/v1/New%20Room%20Designer/yi9wvy9dntpjexsnumds',
            click_link:
              'https://www.castlery.com/us/room-designer?gallery=truep%3D20027%2CaHR0cHM6Ly9yZXMuY2xvdWRpbmFyeS5jb20vY2FzdGxlcnkvaW1hZ2UvcHJpdmF0ZS9lX3RyaW06MTUsYl90cmFuc3BhcmVudCxjX2ZpdCxmX2F1dG8scV9hdXRvLHdfNzUwL3YxNjg0MjMzNDU5L2NydXNhZGVyL3ZhcmlhbnRzLzQwMzcwMDQyL0Rlc2ktV29vbC1SdWctOF8teC0xMF8tU3BlY2tsZWQtQmVpZ2UtMTY4NDIzMzQ1OS5qcGc%3D%2C244%2C305%2C476.53061224489784%2C458.46938775510193%2Cn%2Cn%2Cundefined%2C0%2CRugs%3Arectangle%2Cundefined%2C%2C%2C&p=17010%2C424.48979591836735%2C433.97959183673476%2Cn%2Cn%2C0%2C%2C0%2C%2C%2C&p=22080%2C263.265306122449%2C276.8367346938776%2Cn%2Cn%2C0%2C%2C0%2C%2C%2C&p=20846%2C502.0408163265305%2C362.5510204081633%2Cn%2Cn%2C0%2C%2C0%2C%2C%2C&w=%23b0b0b0&f=%23EAC9A4&s=595&h=297&t=wood&d=plain&V=1&b=f&i=us-3',
          },
        ],
        [
          'Isaac',
          {
            image_link: desktop
              ? 'https://res.cloudinary.com/castlery/image/upload/f_auto,q_auto/v1/New%20Room%20Designer/sxuxj9bml2owiwyci9s8'
              : 'https://res.cloudinary.com/castlery/image/upload/f_auto,q_auto/v1/New%20Room%20Designer/yrii0tmsefkohopu9nu7',
            click_link:
              'https://www.castlery.com/us/room-designer?&gallery=truep=20039%2CaHR0cHM6Ly9yZXMuY2xvdWRpbmFyeS5jb20vY2FzdGxlcnkvaW1hZ2UvcHJpdmF0ZS9lX3RyaW06MTUsYl90cmFuc3BhcmVudCxjX2ZpdCxmX2F1dG8scV9hdXRvLHdfNzUwL3YxNjg0MjMzMTgzL2NydXNhZGVyL3ZhcmlhbnRzLzQwMzcwMDUwL0F6aXphLVJ1Zy04Xy14LTEwXy1XaGl0ZS1CbGFjay0xNjg0MjMzMTgyLmpwZw%3D%3D%2C244%2C305%2C490.81632653061234%2C451.32653061224494%2Cn%2Cn%2Cundefined%2C0%2CRugs%3Arectangle%2Cundefined%2C%2C%2C&p=20290%2C463.26530612244915%2C365.61224489795916%2Cn%2Cn%2C0%2C%2CNaN%2C%2C%2C&p=20288%2C558.1632653061224%2C429.8979591836735%2Cn%2Cn%2C0%2C%2Cundefined%2C%2C%2C&p=19354%2C718.3673469387754%2C361.5306122448979%2Cn%2Cn%2C0%2C%2Cundefined%2C%2C%2C&p=22076%2C718.3673469387754%2C271.734693877551%2Cn%2Cn%2C0%2C%2Cundefined%2C%2C%2C&p=20312%2C816.3265306122449%2C411.53061224489795%2Cn%2Cn%2C0%2C%2Cundefined%2C%2C%2C&w=%23b0b0b0&f=%23EAC9A4&s=595&h=297.5&t=wood&d=plain&V=1&b=f&i=us-1',
          },
        ],
        [
          'Remi',
          {
            image_link: desktop
              ? 'https://res.cloudinary.com/castlery/image/upload/f_auto,q_auto/v1/New%20Room%20Designer/riilzod4yebhcgryyoha'
              : 'https://res.cloudinary.com/castlery/image/upload/f_auto,q_auto/v1/New%20Room%20Designer/cxmgpagtrx92coqcromt',
            click_link:
              'https://www.castlery.com/us/room-designer?gallery=truep%3D20033%2CaHR0cHM6Ly9yZXMuY2xvdWRpbmFyeS5jb20vY2FzdGxlcnkvaW1hZ2UvcHJpdmF0ZS9lX3RyaW06MTUsYl90cmFuc3BhcmVudCxjX2ZpdCxmX2F1dG8scV9hdXRvLHdfNzUwL3YxNjg0MjM0MjM2L2NydXNhZGVyL3ZhcmlhbnRzLzQwMzcwMDQ2L0xvcmVuem8tV29vbC1SdWctOF8teC0xMF8tV2hpdGUtQmxhY2stMTY4NDIzNDIzNi5qcGc%3D%2C244%2C305%2C482%2C445%2Cn%2Cn%2Cundefined%2C0%2CRugs%3Arectangle%2CNaN%2C%2C%2C&p=20568%2C487%2C327%2Cn%2Cn%2C0%2C%2C0%2C10595396%2C12889015%2C08807b313e72c1e3d864eb4449bb56a3d896c3d1c0a1ba071db46488ffe5e6b9&p=17012%2C478%2C426%2Cn%2Cn%2C0%2C%2C0%2C14985902%2C21358630%2Ca286c5c34b4e7f13f5c9b1cb67c1cce739ba4db81037ad1b1bbbda94e5a07a48&p=20579%2C768%2C412%2Cn%2Cn%2C0%2C%2C30%2C46340915%2C54827052%2Cc23a1fb1d4f9087ed4303f8e2e7fd7b5f54f73cebb3b255e2e9484b92d2e1db0&p=19706%2C155%2C361%2Cn%2Cn%2C0%2C%2CNaN%2C%2C%2C&w=%23b0b0b0&f=%23EAC9A4&s=518&h=259&t=wood&d=plain&V=1&b=f&i=us-2',
          },
        ],
        [
          'Dawson',
          {
            image_link: desktop
              ? 'https://res.cloudinary.com/castlery/image/upload/f_auto,q_auto/v1/New%20Room%20Designer/aah9rnlvddvorvklzqra'
              : 'https://res.cloudinary.com/castlery/image/upload/f_auto,q_auto/v1/New%20Room%20Designer/twsnzktxufrpgqlt6n4d',
            click_link:
              'https://www.castlery.com/us/room-designer?&gallery=truep=22078%2C184%2C272%2Cn%2Cn%2C0%2C%2CNaN%2C%2C%2C&p=22610%2C494%2C345%2Cn%2Cn%2C0%2C%2CNaN%2C%2C%2C&p=22114%2C658%2C407%2Cn%2Cn%2C0%2C%2CNaN%2C%2C%2C&p=22116%2C126%2C401%2Cn%2Cn%2C0%2C%2CNaN%2C%2C%2C&p=21707%2C414%2C437%2Cn%2Cn%2C0%2C%2CNaN%2C%2C%2C&p=21709%2C821%2C375%2Cn%2Cn%2C0%2C%2CNaN%2C%2C%2C&w=%23b0b0b0&f=%23EAC9A4&s=595&h=297&t=wood&d=plain&V=1&b=f&i=us-1',
          },
        ],
        [
          'Mori',
          {
            image_link: desktop
              ? 'https://res.cloudinary.com/castlery/image/upload/f_auto,q_auto/v1/New%20Room%20Designer/w0cmi8npi1g6g7qghqea'
              : 'https://res.cloudinary.com/castlery/image/upload/f_auto,q_auto/v1/New%20Room%20Designer/uyhg5xavyetr2zosrzkx',
            click_link:
              'https://www.castlery.com/us/room-designer?gallery=truep%3D21808%2C558%2C457%2Cn%2Cn%2C0%2C%2CNaN%2C%2C%2C&p=22579%2C409%2C383%2Cn%2Cn%2C0%2C%2C0%2C%2C%2C&p=22572%2C834%2C450%2Cn%2Cn%2C0%2C%2C0%2C%2C%2C&p=22557%2C830%2C352%2Cn%2Cn%2C0%2C%2C0%2C%2C%2C&w=%23b0b0b0&f=%23EAC9A4&s=595&h=297&t=wood&d=plain&V=1&b=f&i=us-3',
          },
        ],
        [
          'Pebble',
          {
            image_link: desktop
              ? 'https://res.cloudinary.com/castlery/image/upload/f_auto,q_auto/v1/New%20Room%20Designer/itfrje6ropujllxdoju3'
              : 'https://res.cloudinary.com/castlery/image/upload/f_auto,q_auto/v1/New%20Room%20Designer/w9di1kllsrefrwf7034v',
            click_link:
              'https://www.castlery.com/us/room-designer?p=22082%2C132%2C230%2Cn%2Cn%2C0%2C%2C0%2C%2C%2C&p=19807%2C860%2C422%2Cn%2Cn%2C0%2C%2C0%2C%2C%2C&p=19354%2C129%2C317%2Cn%2Cn%2C0%2C%2C0%2C%2C%2C&p=19393%2C461%2C306%2Cn%2Cn%2C0%2C%2C0%2C%2C%2C&p=17013%2C562%2C413%2Cn%2Cn%2C0%2C%2C0%2C%2C%2C&w=%23b0b0b0&f=%23EAC9A4&s=457&h=228&t=wood&d=plain&V=1&b=f&i=us-2',
          },
        ],
      ]),
    [desktop]
  );
  const defaultBackgrounds = useMemo(() => {
    return {
      SG: desktop
        ? 'https://res.cloudinary.com/castlery/image/upload/v1686126461/static/room-designer/banner_default_background_sg.png'
        : 'https://res.cloudinary.com/castlery/image/upload/v1686126869/static/room-designer/banner_default_background_sg_mobile.png',
      AU: desktop
        ? 'https://res.cloudinary.com/castlery/image/upload/v1686126461/static/room-designer/banner_default_background_au.png'
        : 'https://res.cloudinary.com/castlery/image/upload/v1686126869/static/room-designer/banner_default_background_au_mobile.png',
      US: desktop
        ? 'https://res.cloudinary.com/castlery/image/upload/v1686126461/static/room-designer/banner_default_background_us.png'
        : 'https://res.cloudinary.com/castlery/image/upload/v1686126869/static/room-designer/banner_default_background_us_mobile.png',
      CA: desktop
        ? 'https://res.cloudinary.com/castlery/image/upload/v1686126461/static/room-designer/banner_default_background_us.png'
        : 'https://res.cloudinary.com/castlery/image/upload/v1686126869/static/room-designer/banner_default_background_us_mobile.png',
      UK: desktop
        ? 'https://res.cloudinary.com/castlery/image/upload/v1686126461/static/room-designer/banner_default_background_us.png'
        : 'https://res.cloudinary.com/castlery/image/upload/v1686126869/static/room-designer/banner_default_background_us_mobile.png',
    };
  }, [desktop]);

  // 获取产品集合信息
  const collection = useMemo(() => {
    const taxon = product?.taxons?.find((t: any) => t.level === 1 && t.ancestors?.[0] === 'Collections');
    return taxon?.name?.split(' ')[0] || '';
  }, [product]);

  // 检查产品是否在体验中存在
  const checkProduct = useCallback((productId: string) => {
    const roomDesignerExperienceLabel = HULLA_EXPERIENCE_LABEL[EcEnv.NEXT_PUBLIC_COUNTRY];
    if (typeof window !== 'undefined' && window.HIntegrate) {
      window.HIntegrate.getProductInExperience(roomDesignerExperienceLabel, productId)
        .then((productResult: any) => {
          setIsLoaded(true);
          if (productResult) {
            setIsExist(true);
            if (productResult?.image) {
              setImageSrc(productResult.image);
            }
          } else {
            setIsExist(false);
            setImageSrc('');
          }
        })
        .catch((err: any) => {
          logger.error('HIntegrate error', { error: err });
          setIsLoaded(true);
          setIsExist(false);
          setImageSrc('');
        });
    }
  }, []);

  // 初始化 HIntegrate 脚本
  useEffect(() => {
    if (!variant?.id) return;

    let isMounted = true;

    // 使用统一的脚本加载器，确保只加载一次
    hintegrateLoader
      .load()
      .then(() => {
        if (isMounted && variant?.id) {
          checkProduct(variant.id.toString());
        }
      })
      .catch((err) => {
        logger.error('Failed to load HIntegrate script', { error: err });
        setIsLoaded(true);
        setIsExist(false);
      });

    return () => {
      isMounted = false;
    };
  }, [checkProduct, variant?.id]);

  // 处理 variant 变化，设置工具状态
  useEffect(() => {
    if (!variant) return;

    const {
      furniture_tool_url,
      modular_tool_url,
      configurator_tool_banner_desktop,
      configurator_tool_banner_mobile,
      room_designer_banner_desktop,
      room_designer_banner_mobile,
    } = variant;

    const configuratorToolBannerDesktop = configurator_tool_banner_desktop?.path
      ? {
          videoPath: configurator_tool_banner_desktop.path,
        }
      : configurator_tool_banner_desktop?.links?.large || '';
    const configuratorToolBannerMobile = configurator_tool_banner_mobile?.path
      ? {
          videoPath: configurator_tool_banner_mobile.path,
        }
      : configurator_tool_banner_mobile?.links?.large || '';

    const roomDesignerBannerDesktop = room_designer_banner_desktop?.path
      ? {
          videoPath: room_designer_banner_desktop.path,
        }
      : room_designer_banner_desktop?.links?.large || '';
    const roomDesignerBannerMobile = room_designer_banner_mobile?.path
      ? {
          videoPath: room_designer_banner_mobile.path,
        }
      : room_designer_banner_mobile?.links?.large || '';

    // 设置新房间工具
    if (furniture_tool_url && roomDesignerBannerDesktop && roomDesignerBannerMobile) {
      setNewRoomTool({
        furniture_tool_url,
        roomDesignerBannerDesktop,
        roomDesignerBannerMobile,
      });
      setNewRoomShow(true);
    } else {
      setNewRoomShow(false);
    }

    // 设置模块化工具
    if (modular_tool_url && configuratorToolBannerDesktop && configuratorToolBannerMobile) {
      setModularTool({
        modular_tool_url,
        configuratorToolBannerDesktop,
        configuratorToolBannerMobile,
      });
      setModularShow(true);
    } else {
      setModularShow(false);
    }
  }, [variant]);

  // 处理 A/B 测试显示
  useEffect(() => {
    if (dyRoomDesigner?.experiment === 'B' && RoomDesignerABTestResponsive.get(collection)) {
      setTestShow(true);
    } else {
      setTestShow(false);
    }
  }, [RoomDesignerABTestResponsive, collection, dyRoomDesigner]);

  // 获取图片源
  const getPictureSource = useCallback(() => {
    if (isLoaded) {
      if (isTestShow) {
        return RoomDesignerABTestResponsive.get(collection)?.image_link;
      }
      if (!isExist) {
        return defaultBackgrounds[EcEnv.NEXT_PUBLIC_COUNTRY];
      }
    }
    return blankBackgrounds[EcEnv.NEXT_PUBLIC_COUNTRY];
  }, [defaultBackgrounds, isLoaded, isTestShow, isExist, RoomDesignerABTestResponsive, collection]);

  // 获取链接URL
  const getLinkUrl = useCallback(() => {
    if (modularShow) {
      if ((dyModularTool && dyModularTool.modularBannerShow === 'true') || !dyModularTool) {
        return modularTool.modular_tool_url;
      }
    }
    if (isTestShow) {
      return RoomDesignerABTestResponsive.get(collection)?.click_link;
    }
    if (newRoomShow) {
      return newRoomTool.furniture_tool_url;
    }
    return `/${EcEnv.NEXT_PUBLIC_COUNTRY.toLowerCase()}${basePageConfig['room-designer']}${
      isExist && variant?.id ? `?p=${variant?.id}` : ''
    }`;
  }, [
    modularShow,
    isTestShow,
    newRoomShow,
    isExist,
    variant?.id,
    dyModularTool,
    modularTool.modular_tool_url,
    RoomDesignerABTestResponsive,
    collection,
    newRoomTool.furniture_tool_url,
  ]);

  // 点击事件处理
  const handleClick = useCallback(() => {
    // TODO: 实现跟踪事件
    // dispatch({ type: EVENT_PDP_ROOM_DESIGNER });

    if (modularShow && dyModularTool) {
      // TODO: 实现 DY 报告点击
      // reportClickEngagement({
      //   decisionId: dyModularTool.decisionId,
      //   variationId: dyModularTool.variationId,
      // });
      // dispatch({
      //   type: EVENT_DY_AB_TEST,
      //   result: {
      //     campaignName: 'Modular Tool',
      //     variation: dyModularTool.type,
      //   },
      // });
    }

    if (isTestShow) {
      // TODO: 实现 DY 报告点击
      // reportClickEngagement({
      //   decisionId: dyRoomDesigner.decisionId,
      //   variationId: dyRoomDesigner.variationId,
      // });
      // dispatch({
      //   type: EVENT_DY_AB_TEST,
      //   result: {
      //     campaignName: 'Room Designer',
      //     variation: dyRoomDesigner.experiment,
      //   },
      // });
    }
  }, [modularShow, dyModularTool, isTestShow]);

  // 渲染横幅内容
  const renderBannerContent = useCallback(() => {
    const shouldShowDefaultBanner =
      (!modularShow || (dyModularTool && dyModularTool.modularBannerShow === 'false')) && (!newRoomShow || isTestShow);

    if (shouldShowDefaultBanner) {
      return (
        <FortressImage
          src={getPictureSource() || ''}
          alt="Room Designer Background"
          ratio={desktop ? 2.78 : 1.85}
          lazy={false}
          objectFit="cover"
          sizes={['0.4-md', '1-sm', '1-xs']}
        />
      );
    }

    let videoSrc = '';
    let imageSrc = '';
    let altText = '';

    if (modularShow && ((dyModularTool && dyModularTool.modularBannerShow === 'true') || !dyModularTool)) {
      altText = 'Modular Tool';
      const bannerSource = desktop
        ? modularTool?.configuratorToolBannerDesktop
        : modularTool?.configuratorToolBannerMobile;

      if (typeof bannerSource === 'string') {
        imageSrc = bannerSource;
      } else if (bannerSource && typeof bannerSource === 'object') {
        videoSrc = bannerSource?.videoPath;
      }
    }

    if (
      newRoomShow &&
      !isTestShow &&
      ((dyModularTool && dyModularTool.modularBannerShow === 'false') || !modularShow)
    ) {
      altText = 'Room Designer';
      const bannerSource = desktop ? newRoomTool?.roomDesignerBannerDesktop : newRoomTool?.roomDesignerBannerMobile;
      if (typeof bannerSource === 'string') {
        imageSrc = bannerSource;
      } else if (bannerSource && typeof bannerSource === 'object') {
        // 这是视频配置
        videoSrc = bannerSource?.videoPath;
      }
    }

    if (imageSrc) {
      return (
        <FortressImage
          src={imageSrc}
          alt={`${altText} Background`}
          ratio={desktop ? 2.78 : 1.85}
          sizes={['0.4-md', '1-sm', '1-xs']}
        />
      );
    }

    if (videoSrc) {
      return (
        <FortressVideo
          src={videoSrc}
          muted
          loop
          autoPlay
          controls={false}
          containerConfig={{
            aspectRatio: desktop ? 2.78 : 1.6,
            objectFit: 'fill',
          }}
          videoConfig={{
            aspectRatio: desktop ? 2.78 : 1.6,
          }}
          lazyLoad={true}
          sx={{
            backgroundColor: 'transparent',
          }}
        />
      );
    }

    return null;
  }, [
    modularShow,
    dyModularTool,
    newRoomShow,
    isTestShow,
    getPictureSource,
    desktop,
    modularTool?.configuratorToolBannerDesktop,
    modularTool?.configuratorToolBannerMobile,
    newRoomTool?.roomDesignerBannerDesktop,
    newRoomTool?.roomDesignerBannerMobile,
  ]);

  // 获取描述文本
  const getDescriptionText = useCallback(() => {
    if ((dyModularTool && dyModularTool.modularBannerShow === 'false') || !modularShow) {
      return "See how our furniture pairs with other pieces you've been eyeing-no heavy lifting required.";
    }
    return 'Combine different elements of your favorite furniture and create your ideal configuration.';
  }, [dyModularTool, modularShow]);

  // 获取链接文本
  const getLinkText = useCallback(() => {
    if ((dyModularTool && dyModularTool.modularBannerShow === 'false') || !modularShow) {
      return 'Try our room designer tool';
    }
    return 'Try our configurator tool';
  }, [dyModularTool, modularShow]);

  // Intersection Observer 处理
  const handleIntersect = useCallback((entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        // TODO: 实现跟踪事件
        // if (modularShow && dyModularTool) {
        //   dispatch({
        //     type: EVENT_DY_EVENT,
        //     result: {
        //       detailAction: 'impression_modular_tool_ab_test',
        //       label: `variant ${dyModularTool?.type}`,
        //     },
        //   });
        // }
        // if (dyRoomDesigner && (dyModularTool?.modularBannerShow === 'false' || !modularShow)) {
        //   dispatch({
        //     type: EVENT_DY_EVENT,
        //     result: {
        //       detailAction: 'impression_room_designer_ab_test',
        //       label: `variant ${dyRoomDesigner?.experiment}`,
        //     },
        //   });
        // }
        setObserveOnce(false);
      }
    });
  }, []);

  // 设置 Intersection Observer
  useEffect(() => {
    if (!observerRef.current || observeOnce || (!dyRoomDesigner && !dyModularTool) || !IntersectionObserver) return;

    const observer = new IntersectionObserver(handleIntersect, {
      root: null,
      rootMargin: '0px',
      threshold: 1.0,
    });

    observer.observe(observerRef.current);
    setObserveOnce(true);

    return () => {
      observer.disconnect();
    };
  }, [handleIntersect, dyRoomDesigner, dyModularTool, observeOnce]);

  // 如果没有相关数据，不渲染组件
  if (!variant?.id) {
    return null;
  }

  return (
    <Sheet
      variant="solid"
      sx={{
        width: '100%',
        position: 'relative',
      }}
      data-panel={variant?.id}
    >
      {/* 横幅区域 */}
      <Box
        ref={observerRef}
        sx={{
          position: 'relative',
          cursor: 'pointer',
          width: '100%',
        }}
        component={CustomLink}
        href={getLinkUrl()}
        onClick={handleClick}
        aria-label={`${getLinkText()} - Click to view details`}
      >
        {renderBannerContent()}

        {(dyModularTool?.modularBannerShow === 'false' || !modularShow) && !isTestShow && !newRoomShow && imageSrc && (
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              bottom: desktop ? '7%' : '13%',
              transform: 'translateX(-50%)',
              width: desktop ? '44%' : '53%',
              border: '2px dashed #323433',
              padding: '0 2px',
            }}
          >
            <FortressImage
              src={imageSrc}
              alt="Try our Room Designer tool"
              ratio={2.5}
              sizes={['0.4-md', '1-sm', '1-xs']}
            />
            <FortressImage
              src="https://res.cloudinary.com/castlery/image/upload/v1684382730/static/room-designer/icon_drag.png"
              alt="Try our Room Designer tool - drag icon"
              sx={{
                width: '24px',
                height: '24px',
                position: 'absolute',
                left: '-12px',
                top: ' -12px',
              }}
              sizes="24px"
            />
            <FortressImage
              src="https://res.cloudinary.com/castlery/image/upload/v1684382729/static/room-designer/icon_delete.png"
              alt="Try our Room Designer tool - delete icon"
              sx={{
                width: '24px',
                height: '24px',
                position: 'absolute',
                right: '-12px',
                top: ' -12px',
              }}
              sizes="24px"
            />
          </Box>
        )}
      </Box>

      <Stack py={mobile ? 4 : 6} px={4}>
        {/* 描述文本 */}
        <Typography
          level="body2"
          sx={{
            color: 'var(--fortress-palette-neutral-plainColor)',
          }}
        >
          {getDescriptionText()}
        </Typography>

        {/* 行动按钮 */}
        <Link
          href={getLinkUrl()}
          onClick={handleClick}
          variant="primary"
          level="body2"
          endDecorator={
            <ArrowRight
              sx={{
                width: '20px',
                height: '20px',
              }}
            />
          }
          sx={(theme: Theme) => ({
            marginTop: theme.spacing(2),
          })}
        >
          {getLinkText()}
        </Link>
      </Stack>
    </Sheet>
  );
};
